import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Printer, ScrollText, Compass, Award, Download, Linkedin, Twitter, Copy, Check } from "lucide-react";

/**
 * AdventureWrapUp — the ceremony a learner unlocks after completing all
 * twelve adventures.  A single unrolled scroll, warm and quiet: totals,
 * a per-adventure breakdown with chapter counts, and hand-signed lines
 * from every recurring character.  No confetti — Ms. Pradhan hates it.
 */
export default function AdventureWrapUp() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/adventures/wrap-up/me")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Sign in to see your scroll."));
  }, []);

  if (err) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="wrapup-error">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-3">The scroll isn't ready yet.</h1>
        <p className="text-ink2 mb-5">{err}</p>
        <Link to="/login" className="btn-primary text-sm">Sign in</Link>
      </div>
    );
  }
  if (!data) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;

  const completedCount = data.total_completed;
  const total = data.total_available;
  const eligible = data.eligible;

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="wrapup-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-ink2 hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to your dashboard
      </Link>

      {!eligible ? (
        <NotEligibleYet data={data} />
      ) : (
        <div className="relative rounded-3xl border-2 border-ink bg-cream shadow-[10px_10px_0_0_#1F1A17] overflow-hidden print:shadow-none print:border" data-testid="wrapup-scroll">
          {/* Scroll top edge */}
          <div className="h-4 bg-marigold/25 border-b-2 border-ink" aria-hidden="true" />
          <div className="p-8 md:p-12 relative">
            {/* Corner ornaments */}
            <div className="absolute top-6 left-6 w-4 h-4 border-l-2 border-t-2 border-marigold" aria-hidden="true" />
            <div className="absolute top-6 right-6 w-4 h-4 border-r-2 border-t-2 border-marigold" aria-hidden="true" />
            <div className="absolute bottom-6 left-6 w-4 h-4 border-l-2 border-b-2 border-marigold" aria-hidden="true" />
            <div className="absolute bottom-6 right-6 w-4 h-4 border-r-2 border-b-2 border-marigold" aria-hidden="true" />

            <div className="text-center mb-8">
              <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-marigold mb-3 inline-flex items-center gap-2">
                <ScrollText className="w-3.5 h-3.5" /> Wrap-up scroll · Twelve of twelve
              </div>
              <h1 className="font-heading text-4xl md:text-6xl font-semibold text-ink leading-[1.05] mb-2" data-testid="wrapup-heading">
                For {data.learner_name},
              </h1>
              <div className="font-hand text-marigold text-2xl md:text-3xl mt-1">who walked the whole road.</div>
            </div>

            <p className="text-ink2 text-base md:text-lg text-center max-w-2xl mx-auto italic leading-relaxed mb-10">
              You have walked all <span className="font-heading text-ink not-italic font-semibold">twelve adventures</span> — from the day the internet went out in your kitchen to the coastal siren that rings on every second Wednesday, from a bazaar keeping four hundred ledgers to a keypad phone that turned into a newsroom. In total: <span className="font-heading text-ink not-italic font-semibold">{data.grand_total_chapters} chapters</span> walked alongside the story.
            </p>

            {/* Adventures roll */}
            <div className="mb-10">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold text-center mb-4">The twelve, in order</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm font-heading">
                {data.adventures.map((a) => (
                  <div key={a.slug} className="flex items-baseline gap-3 border-b border-dashed border-ink/15 py-1.5" data-testid={`wrapup-adv-${a.slug}`}>
                    <span className="font-mono text-[10px] text-marigold uppercase tracking-widest w-16 flex-shrink-0">{a.kicker.replace("Adventure ", "")}</span>
                    <span className="flex-1 text-ink truncate">{a.title}</span>
                    <span className="text-[11px] font-mono text-ink2/70">{a.chapter_count} ch</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-baseline justify-end gap-3 text-sm font-heading pr-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-marigold">Grand total</span>
                <span className="font-heading font-semibold text-ink">{data.grand_total_chapters} chapters</span>
              </div>
            </div>

            {/* Signatures */}
            <div className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold text-center mb-5">Signed, by everyone who walked with you</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="wrapup-signatures">
                {data.signatures.map((s, i) => (
                  <div key={i} className="border-l-2 border-marigold pl-4 py-1" data-testid={`wrapup-sig-${i}`}>
                    <div className="font-hand text-2xl text-ink leading-tight mb-1">— {s.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink2/70 mb-2">{s.role}</div>
                    <div className="italic text-sm text-ink leading-snug">“{s.line}”</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center text-xs text-ink2/70 italic mt-8 pt-6 border-t border-ink/15">
              First step: <span className="font-mono normal-case tracking-normal">{formatDate(data.first_completion_at)}</span>
              <span className="mx-2">·</span>
              Last step: <span className="font-mono normal-case tracking-normal">{formatDate(data.last_completion_at)}</span>
            </div>
          </div>
          <div className="h-4 bg-marigold/25 border-t-2 border-ink" aria-hidden="true" />

          {/* Print + share bar (hidden in print) */}
          <div className="print:hidden absolute top-8 right-8 flex items-center gap-2 z-10">
            <DownloadPngButton learnerName={data.learner_name} />
            <button
              onClick={() => window.print()}
              className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
              data-testid="wrapup-print"
            >
              <Printer className="w-3.5 h-3.5" /> Print scroll
            </button>
          </div>
          <ShareBar data={data} />
        </div>
      )}
    </div>
  );
}

function NotEligibleYet({ data }) {
  const remaining = data.adventures.filter((a) => !a.completed && !a.is_epilogue);
  return (
    <div className="rounded-3xl border-2 border-dashed border-ink/30 p-10" data-testid="wrapup-not-eligible">
      <div className="text-center mb-6">
        <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-marigold mb-2 inline-flex items-center gap-1.5">
          <Award className="w-3.5 h-3.5" /> The scroll is patient
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-ink leading-tight mb-2">
          {data.total_completed} of {data.total_available} adventures walked.
        </h1>
        <p className="text-ink2 max-w-xl mx-auto">
          Finish the remaining {data.total_available - data.total_completed} to unlock the wrap-up ceremony. It is not confetti. It is a warm, one-page scroll signed by every character.
        </p>
      </div>
      <div className="max-w-lg mx-auto">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-3 text-center">Still to walk</div>
        <ul className="space-y-1.5" data-testid="wrapup-remaining">
          {remaining.map((a) => (
            <li key={a.slug} className="flex items-center gap-3 border-b border-dashed border-ink/15 py-1.5 text-sm">
              <span className="font-mono text-[10px] text-marigold uppercase tracking-widest w-16 flex-shrink-0">{a.kicker.replace("Adventure ", "")}</span>
              <Link to={`/adventures/${a.slug}`} className="flex-1 text-ink hover:text-marigold font-heading transition">
                {a.title}
              </Link>
              <ArrowRight className="w-3.5 h-3.5 text-ink2/50" />
            </li>
          ))}
        </ul>
        <div className="mt-6 text-center">
          <Link to="/adventures" className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="wrapup-cta-adventures">
            <Compass className="w-4 h-4" /> Pick the next one
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

function DownloadPngButton({ learnerName }) {
  const [busy, setBusy] = useState(false);
  const onDownload = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get("/adventures/wrap-up/me/scroll.png", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wrap-up-${(learnerName || "learner").replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // best-effort — no toast import here; keep quiet
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onDownload}
      disabled={busy}
      className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
      data-testid="wrapup-download-png"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      Download PNG
    </button>
  );
}

function ShareBar({ data }) {
  const [copied, setCopied] = useState(false);
  const wallUrl = typeof window !== "undefined"
    ? `${window.location.origin}/wall/${encodeURIComponent(data.learner_handle || "")}`
    : "";
  const captionCore = `I walked all ${data.total_core || 12} Durga Programming Adventures — ${data.grand_total_chapters} chapters, one Ms. Pradhan, a lot of tea.`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(wallUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(captionCore + "\n\n")}&url=${encodeURIComponent(wallUrl)}`;

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(`${captionCore}\n\n${wallUrl}`);
      setCopied(true);
      toast.success("Caption copied — paste it anywhere.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — try selecting the caption manually.");
    }
  };

  return (
    <div
      className="print:hidden mx-8 md:mx-12 mt-6 mb-8 rounded-2xl border border-ink/25 bg-cream/60 p-4"
      data-testid="wrapup-share-bar"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Share the walk</div>
          <p className="text-sm text-ink2 italic leading-snug">
            &ldquo;{captionCore}&rdquo;
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
            data-testid="wrapup-share-linkedin"
          >
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </a>
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
            data-testid="wrapup-share-twitter"
          >
            <Twitter className="w-3.5 h-3.5" /> X / Twitter
          </a>
          <button
            onClick={copyCaption}
            className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
            data-testid="wrapup-share-copy"
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy caption</>}
          </button>
        </div>
      </div>
    </div>
  );
}
